// Hard paywall that fires automatically the first time a user crosses out
// of Level 1. Mounted once at app root (above the router) so it works no
// matter where the user happens to be when they earn their first XP — quest
// completion on /build, daily hack on /, Hub action on /hack, etc.
//
// Behavior:
//  - Watches `useLevelOneComplete`. The hook is `false → true` exactly once
//    per browser (cached in localStorage). We diff the previous value vs the
//    current one and treat the rising edge as "just unlocked".
//  - On that rising edge we open `UpgradeWallModal` pinned to the Operator
//    tier ($20, the Level-2 plan) and stamp a separate localStorage flag so
//    the modal is never re-shown — even after the user dismisses it, signs
//    out, signs back in, or earns more XP later.
//  - Small `setTimeout` delay so the modal slides in after the existing
//    `LevelUpCelebration` overlay finishes its entry animation. Without it
//    the two would stack on top of each other and feel chaotic.
//
// We deliberately do NOT auto-open for users who completed Level 1 in a
// previous session (cache already `true` on first render): the paywall is
// a "moment-of-celebration" surface, not a nag screen.
import { useEffect, useRef, useState } from "react";
import UpgradeWallModal from "@/components/UpgradeWallModal";
import { useAuth } from "@/hooks/useAuth";
import { useLevelOneComplete } from "@/hooks/useLevelOneComplete";

const SHOWN_KEY = "hl_level2_paywall_shown";

const readShown = (): boolean => {
  if (typeof window === "undefined") return false;
  try {
    return window.localStorage.getItem(SHOWN_KEY) === "1";
  } catch {
    return false;
  }
};

const markShown = () => {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(SHOWN_KEY, "1");
  } catch {
    /* localStorage unavailable — best-effort, never break UX */
  }
};

const Level2PaywallGate = () => {
  const { user } = useAuth();
  const levelOneDone = useLevelOneComplete();
  const [open, setOpen] = useState(false);
  // Tracks the previous value of `levelOneDone` so we can detect the
  // false→true rising edge. Initialized from the hook's first render so
  // returning users (whose cache is already `true`) don't trigger.
  const prevDoneRef = useRef<boolean>(levelOneDone);

  useEffect(() => {
    if (!user) {
      // Reset the local edge tracker on sign-out. The localStorage "shown"
      // flag persists per-browser, which is intentional — once a visitor
      // has seen the paywall on this device, we don't want to re-show it
      // when they switch accounts (or sign in/out for testing).
      prevDoneRef.current = false;
      return;
    }
    const wasDone = prevDoneRef.current;
    prevDoneRef.current = levelOneDone;
    // Rising edge: not-done → done. This is the moment of unlock.
    if (!wasDone && levelOneDone && !readShown()) {
      markShown();
      // Delay so the LevelUpCelebration overlay (fired by useXPGainToasts)
      // has time to play first. ~1.6s lines up with its exit animation.
      const t = window.setTimeout(() => setOpen(true), 1600);
      return () => window.clearTimeout(t);
    }
  }, [user, levelOneDone]);

  return (
    <UpgradeWallModal
      open={open}
      onClose={() => setOpen(false)}
      suggestedTier="operator"
      reason="Level 1 cleared. Ready for Level 2?"
    />
  );
};

export default Level2PaywallGate;
